import React from 'react';
import { CalendarRange, ChevronRight, Copy, QrCode, Link2, ExternalLink } from 'lucide-react';
import UiTooltip from '../ui/Tooltip';
import EventStatusBadge from './EventStatusBadge';
import EventActionsMenu from './EventActionsMenu';
import { getEventStatus } from '../../utils/eventHelpers';
import { formatDate } from '../../utils/formatDate';
import styles from './EventCard.module.css';

const APP_URL = window.location.origin;

export default function EventCard({
    event, onEdit, onDelete, onDuplicate, onTransfer, onExportCsv, onQr, onToast,
    deletingId, exportingEventId, duplicatingEventId, onClick, view = 'grid',
}) {
    const status = getEventStatus(event);
    const joinUrl = `${APP_URL}/join/${event.code}`;

    const copyToClipboard = (text, label) => async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            onToast(`${label} copied!`);
        } catch {
            onToast('Copy failed');
        }
    };

    return (
        <div className={`${styles.eventCard} ${view === 'list' ? styles.eventCardList : ''}`} onClick={onClick}>
            <div className={styles.cardAccent} />

            <div className={styles.cardHeader}>
                <div className={styles.cardTitleRow}>
                    <h3 className={styles.cardTitle}>{event.name}</h3>
                    <EventStatusBadge status={status} />
                </div>
                <EventActionsMenu
                    event={event}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onTransfer={onTransfer}
                    onExportCsv={onExportCsv}
                    deletingId={deletingId}
                    exportingEventId={exportingEventId}
                    duplicatingId={duplicatingEventId}
                />
            </div>

            <div className={styles.cardMeta}>
                <div className={styles.metaRow}>
                    <CalendarRange size={13} className={styles.metaIcon} />
                    <span className={styles.metaText}>
                        {formatDate(event.start_date) || '—'}
                        {event.end_date && <> <ChevronRight size={11} /> {formatDate(event.end_date)}</>}
                    </span>
                </div>
            </div>

            <div className={styles.codeRow}>
                <div className={styles.codeChip}>
                    <span className={styles.codeLabel}>Code</span>
                    <span className={styles.codeValue}>#{event.code}</span>
                </div>
                <UiTooltip title="Copy event code" placement="top">
                    <button
                        className={styles.copyCodeBtn}
                        onClick={copyToClipboard(event.code, 'Event code')}
                    >
                        <Copy size={12} /> Copy Code
                    </button>
                </UiTooltip>
            </div>

            <div className={styles.cardFooter} onClick={e => e.stopPropagation()}>
                <UiTooltip title="Show QR code" placement="top">
                    <button
                        className={styles.footerAction}
                        onClick={(e) => { e.stopPropagation(); onQr(event); }}
                    >
                        <QrCode size={13} /> QR Code
                    </button>
                </UiTooltip>
                <UiTooltip title="Copy join link" placement="top">
                    <button
                        className={styles.footerAction}
                        onClick={copyToClipboard(joinUrl, 'Join link')}
                    >
                        <Link2 size={13} /> Copy Link
                    </button>
                </UiTooltip>
                <UiTooltip title="Open event" placement="top">
                    <button
                        className={styles.footerActionPrimary}
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                    >
                        Open <ExternalLink size={12} />
                    </button>
                </UiTooltip>
            </div>
        </div>
    );
}
